import { CloseOutlined, SearchOutlined } from '../../../components/icons'
import { isQueryActive } from '../staging-part-search'

export interface StagingToolbarProps {
  readonly query: string
  readonly onQueryChange: (query: string) => void
  /**
   * CRITICAL 3 (Gate-5 review) — the ONE string this component announces AND
   * displays, computed once by `StagingPage` (see its own doc comment on
   * `searchDisclosureMessage`) so there is exactly one `aria-live` region on
   * this page, never one per section. Empty when idle. `StagingPage` is
   * responsible for building it from the UNFILTERED total (D3) — this
   * component has no way to enforce that; it only renders what it is given.
   */
  readonly disclosureMessage: string
}

/**
 * ADR-0155 — the reinstated in-page search, reversing ADR-0124 D6's
 * coordinator ruling for SEARCH ONLY. Per D1: "the reinstated component may
 * be named `StagingToolbar` again, but it is a search input only. It must
 * not grow a filter slot, and it must not re-import the deleted
 * `SegmentedControl` overrides." Nothing here does either.
 *
 * D2 (SUPERSEDED, 2026-09-07) — this component used to promise names only
 * (`Search parts by name…`) rather than the design's literal
 * `Search parts, files, or drawings…` (`Fictiv Platform.dc.html:3049`), on
 * the premise that the latter would misdescribe a name-only haystack.
 * Product ruling: match the design's copy verbatim. This is no longer as
 * much of a stretch as D2 assumed — the 2026-09-04 human ruling on
 * {@link matchesFilename} already added filename matching for
 * needs-resolution rows, so "files" is now literally true; "drawings" is
 * covered in that a needs-resolution row IS frequently an unidentified
 * drawing. The underlying matcher functions are UNCHANGED by this — only
 * the copy differed from what they actually do.
 *
 * D4 — the three MUSTs this component exists to satisfy:
 *   (1) a persistent "showing X of Y" indicator, announced via
 *       `aria-live="polite"` on a region that survives re-render (not a
 *       toast/tooltip a user could miss);
 *   (2) a clear control, visible whenever a query is active, that is a real
 *       focusable `<button>` — never an icon with a bare click handler;
 *   (3) — the distinct empty state itself is NOT this component's concern;
 *       it is rendered by whichever section actually has zero matches
 *       (`StagingPage`'s own sections), since "empty" means something
 *       different in a BOM table vs. a `<ul>`.
 *
 * CRITICAL 3 (Gate-5 review) — the disclosure `<output>` below is now
 * UNCONDITIONALLY mounted, with `disclosureMessage` (empty when idle) as its
 * only content. It used to be `{hasQuery && <p aria-live="polite">…}` —
 * mounting the live region and its first content in the SAME commit, which
 * means a screen reader never announces the FIRST "showing X of Y": per
 * WAI-ARIA, a live region must already exist in the DOM before its content
 * changes, or the change is invisible to assistive tech. This repo already
 * has this exact fix on record — ADR-0124 **W8**, on this very page:
 * "the caption is now an `<output>` polite live region rendered
 * UNCONDITIONALLY (a live region must exist BEFORE its content changes)" —
 * and a live precedent at
 * `verticals/catalog/components/CatalogSeriesFacetSidebar.tsx`'s
 * `.catalog-series-facet-pending` output. ADR-0155 D4's own "a region that
 * survives re-render" phrase was already saying this; it just was not
 * implemented that way.
 *
 * D4(5) — no default/remembered/URL-restored query: `query` is fully
 * controlled by the caller, which is the page-level `useState('')` that
 * starts empty on every mount and is never read from the URL.
 *
 * Placed at PAGE level (not nested inside the BOM-only "BOM parts" section)
 * because D1/D6(a) require search to filter EVERY section uniformly,
 * including the legacy Identified/Matched buckets and Needs-resolution — all
 * of which can render on a session with no captured BOM hierarchy at all
 * (the dominant, permanent case per `StagingPage.tsx`'s own doc comments).
 * Nesting the toolbar inside the BOM section's `hasBomRows`-gated toolbar
 * slot would make the reinstated search invisible on exactly that dominant
 * case, which would itself violate D4's "the user can plainly see it is
 * active" requirement. See this file's citation in the frontend-engineer's
 * handoff notes for the full reasoning.
 */
export function StagingToolbar({ query, onQueryChange, disclosureMessage }: StagingToolbarProps) {
  const hasQuery = isQueryActive(query)

  return (
    <div className="staging-toolbar">
      <div className="staging-search">
        <label className="sr-only" htmlFor="staging-search-input">
          Search parts, files, or drawings
        </label>
        <SearchOutlined aria-hidden="true" className="staging-search-icon" />
        <input
          id="staging-search-input"
          type="search"
          className="staging-search-input"
          placeholder="Search parts, files, or drawings…"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
        />
        {hasQuery && (
          <button
            type="button"
            className="staging-search-clear"
            aria-label="Clear search"
            onClick={() => onQueryChange('')}
          >
            <CloseOutlined aria-hidden="true" />
          </button>
        )}
      </div>
      {/* D4(1) — persistent, announced. Unconditionally mounted (see the
          doc comment above); empty text at rest means nothing is announced
          and nothing is visible, without unmounting the region itself. */}
      <output className="staging-search-summary" aria-live="polite">
        {disclosureMessage}
      </output>
    </div>
  )
}
